

import React, { useState, useEffect } from 'react';
import { formatImageUrl } from '../utils/imageUtils';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart, toggleWishlist, isInWishlist } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get(`/products/${id}`).then(res => {
      setProduct(res.data.product);
      if (res.data.product.colors?.[0]) setSelectedColor(res.data.product.colors[0]);
    }).catch(() => navigate('/shop')).finally(() => setLoading(false));
  }, [id]);

  const handleAddToCart = async () => {
    if (!user) { navigate('/login'); return; }
    if (product.stock === 0) return;
    setAdding(true);
    try {
      await addToCart(product._id, qty, selectedSize, selectedColor);
      setMsg('Added to bag ✓');
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Error adding to cart');
    }
    setAdding(false);
  };

  const handleWishlist = async () => {
    if (!user) { navigate('/login'); return; }
    const action = await toggleWishlist(product._id);
    setMsg(action === 'added' ? 'Added to wishlist ✓' : 'Removed from wishlist');
    setTimeout(() => setMsg(''), 2000);
  };

  if (loading) return (
    <div className="min-h-screen px-6 md:px-12 lg:px-20 py-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        <div className="skeleton aspect-[3/4]" />
        <div className="space-y-4 pt-4">
          <div className="skeleton h-4 w-24" />
          <div className="skeleton h-8 w-64" />
          <div className="skeleton h-4 w-32" />
        </div>
      </div>
    </div>
  );

  if (!product) return null;

  const inWishlist = isInWishlist(product._id);
  const images = product.images?.length > 0 ? product.images : ['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80'];



  return (
    <div className="px-6 md:px-12 lg:px-20 py-10 animate-fade-in">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
        {/* Images */}
        <div className="space-y-3">
          <div className="aspect-[4/5] overflow-hidden bg-[#EFEDE8]">
            <img 
              src={formatImageUrl(images[selectedImage])} 
              alt={product.name} 
              className="w-full h-full object-cover"
              onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80'; }} 
            />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2">
              {images.map((img, i) => (
                <button key={i} onClick={() => setSelectedImage(i)}
                  className={`w-20 h-20 overflow-hidden border-2 transition-colors ${selectedImage === i ? 'border-lux-primary' : 'border-transparent'}`}>
                  <img 
                    src={formatImageUrl(img)} 
                    alt="" 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=200&q=80'; }} 
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="pt-4 lg:pt-10">
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className="label-sm text-lux-gold">{product.brand}</p>
              {product.createdBy && (
                <Link to={`/seller/${product.createdBy._id}`} className="text-[11px] text-gray-400 hover:text-lux-gold font-sans tracking-wider transition-colors mt-1 block">
                  Seller: {product.createdBy.storeName || `${product.createdBy.firstName} ${product.createdBy.lastName}`}
                </Link>
              )}
            </div>
            <button onClick={handleWishlist} className="p-2 hover:scale-110 transition-transform">
              <svg width="20" height="20" viewBox="0 0 24 24" fill={inWishlist ? "#C6A75E" : "none"} stroke={inWishlist ? "#C6A75E" : "#111"} strokeWidth="1.5">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>
          </div>

          <h1 className="font-display text-4xl text-lux-primary font-normal mb-4 leading-tight">{product.name}</h1>

          {/* Price */}
          <div className="flex items-center gap-4 mb-2">
            {product.offerPercentage > 0 ? (
              <>
                <span className="font-display text-3xl text-lux-primary">₹{product.finalPrice?.toFixed(2)}</span>
                <span className="font-sans text-lg text-lux-secondary line-through">₹{product.price?.toFixed(2)}</span>
                <span className="bg-lux-gold text-white text-xs px-2 py-0.5 font-sans">-{product.offerPercentage}%</span>
              </>
            ) : (
              <span className="font-display text-3xl text-lux-primary">₹{product.price?.toFixed(2)}</span>
            )}
          </div>

          {/* Stock & Price Type */}
          <div className="mb-8 flex items-center gap-3">
            {product.stock === 0 ? (
              <span className="text-xs font-sans text-red-500 tracking-wider">Out of Stock</span>
            ) : product.stock < 5 ? (
              <span className="text-xs font-sans text-lux-gold tracking-wider">Only {product.stock} left</span>
            ) : (
              <span className="text-xs font-sans text-lux-secondary tracking-wider">In Stock</span>
            )}
            {product.priceType === 'affordable' && (
              <span className="bg-emerald-50 text-emerald-700 text-[10px] px-2 py-0.5 font-sans tracking-wider border border-emerald-200">Affordable</span>
            )}
          </div>

          <div className="h-px bg-lux-border mb-8" />

          {/* Colors */}
          {product.colors?.length > 0 && (
            <div className="mb-8">
              <p className="label-sm text-lux-secondary mb-3">Color: <span className="text-lux-primary">{selectedColor}</span></p>
              <div className="flex gap-2">
                {product.colors.map(color => (
                  <button key={color} onClick={() => setSelectedColor(color)}
                    className={`px-4 py-2 text-xs font-sans border transition-colors ${selectedColor === color ? 'border-lux-primary bg-lux-primary text-white' : 'border-lux-border text-lux-secondary hover:border-lux-primary'}`}>
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sizes */}
          {product.sizes?.length > 0 && (
            <div className="mb-8">
              <p className="label-sm text-lux-secondary mb-3">Size</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map(size => (
                  <button key={size} onClick={() => setSelectedSize(size)}
                    className={`w-12 h-12 text-xs font-sans border transition-colors ${selectedSize === size ? 'border-lux-primary bg-lux-primary text-white' : 'border-lux-border text-lux-secondary hover:border-lux-primary'}`}>
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mb-8">
            <p className="label-sm text-lux-secondary mb-3">Quantity</p>
            <div className="flex items-center border border-lux-border w-fit">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 text-lux-primary hover:bg-lux-bg transition-colors text-lg">−</button>
              <span className="w-12 text-center text-sm font-sans text-lux-primary">{qty}</span>
              <button onClick={() => setQty(Math.min(product.stock, qty + 1))} className="w-10 h-10 text-lux-primary hover:bg-lux-bg transition-colors text-lg">+</button>
            </div>
          </div>

          {/* CTA */}
          {msg && <div className="mb-4 text-xs font-sans text-lux-gold tracking-wider">{msg}</div>}
          <div className="flex gap-3">
            <button onClick={handleAddToCart} disabled={adding || product.stock === 0}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed text-center">
              {product.stock === 0 ? 'Out of Stock' : (adding ? 'Adding...' : 'Add to Bag')}
            </button>
            <button onClick={handleWishlist} className="btn-outline px-4">
              <svg width="16" height="16" viewBox="0 0 24 24" fill={inWishlist ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>
          </div>

          <div className="h-px bg-lux-border mt-10 mb-8" />

          {/* Description */}
          <div>
            <p className="label-sm text-lux-secondary mb-4">About This Piece</p>
            <p className="font-body text-lux-secondary text-lg leading-relaxed">{product.description}</p>
          </div>

          {/* Details */}
          <div className="mt-8 space-y-3">
            {[
              ['Category', product.category],
              ['Material', 'Premium Quality'],
              ['Care', 'Dry Clean Only'],
              ['Origin', 'Made in Italy']
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2 border-b border-lux-border">
                <span className="label-sm text-lux-secondary">{k}</span>
                <span className="text-xs font-sans text-lux-primary capitalize">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============================== */}
      {/* REVIEWS SECTION */}
      {/* ============================== */}
      <ReviewSection productId={id} user={user} formatImageUrl={formatImageUrl} />
    </div>
  );
}


// ============================================================
// ReviewSection Component
// ============================================================
function ReviewSection({ productId, user, formatImageUrl }) {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [ratingBreakdown, setRatingBreakdown] = useState({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [sort, setSort] = useState('recent');
  const [filter, setFilter] = useState('');
  const [loadingReviews, setLoadingReviews] = useState(true);

  // Can-review state
  const [canReview, setCanReview] = useState(false);
  const [eligibleOrderId, setEligibleOrderId] = useState(null);

  // Review form state
  const [showForm, setShowForm] = useState(false);
  const [formRating, setFormRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [formTitle, setFormTitle] = useState('');
  const [formComment, setFormComment] = useState('');
  const [formImages, setFormImages] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewMsg, setReviewMsg] = useState({ type: '', text: '' });

  // Compute average from breakdown
  const avgRating = total > 0
    ? Math.round((Object.entries(ratingBreakdown).reduce((sum, [star, count]) => sum + star * count, 0) / total) * 10) / 10
    : 0;

  const fetchReviews = async (pg = 1, resetList = false) => {
    try {
      setLoadingReviews(true);
      const params = new URLSearchParams({ sort, page: pg, limit: 5 });
      if (filter) params.set('filter', filter);

      const res = await api.get(`/reviews/product/${productId}?${params}`);
      if (resetList || pg === 1) {
        setReviews(res.data.reviews);
      } else {
        setReviews(prev => [...prev, ...res.data.reviews]);
      }
      setTotal(res.data.total);
      setRatingBreakdown(res.data.ratingBreakdown);
      setPages(res.data.pages);
      setPage(pg);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoadingReviews(false);
    }
  };

  const checkCanReview = async () => {
    if (!user) return;
    try {
      const res = await api.get(`/reviews/can-review/${productId}`);
      setCanReview(res.data.canReview);
      setEligibleOrderId(res.data.orderId || null);
    } catch { }
  };

  useEffect(() => {
    fetchReviews(1, true);
    checkCanReview();
  }, [productId]);

  useEffect(() => {
    fetchReviews(1, true);
  }, [sort, filter]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (formRating === 0) {
      setReviewMsg({ type: 'error', text: 'Please select a star rating' });
      return;
    }
    setSubmitting(true);
    try {
      const imageArr = formImages ? formImages.split('\n').map(s => s.trim()).filter(Boolean) : [];
      await api.post('/reviews', {
        productId,
        orderId: eligibleOrderId,
        rating: formRating,
        title: formTitle,
        comment: formComment,
        images: imageArr
      });
      setReviewMsg({ type: 'success', text: 'Review submitted successfully!' });
      setFormRating(0); setFormTitle(''); setFormComment(''); setFormImages('');
      setShowForm(false);
      setCanReview(false);
      fetchReviews(1, true);
    } catch (err) {
      setReviewMsg({ type: 'error', text: err.response?.data?.message || 'Error submitting review' });
    }
    setSubmitting(false);
    setTimeout(() => setReviewMsg({ type: '', text: '' }), 4000);
  };

  const StarDisplay = ({ rating, size = 16 }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <svg key={star} width={size} height={size} viewBox="0 0 24 24"
          fill={star <= rating ? "#C6A75E" : "none"}
          stroke={star <= rating ? "#C6A75E" : "#D1CBC0"}
          strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto mt-16 border-t border-lux-border pt-12">
      <h2 className="font-display text-3xl text-lux-primary mb-10">Customer Reviews</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Left: Rating Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-lux-border p-8">
            <div className="text-center mb-6">
              <p className="font-display text-5xl text-lux-primary mb-2">{avgRating || '—'}</p>
              <StarDisplay rating={Math.round(avgRating)} size={20} />
              <p className="text-xs font-sans text-lux-secondary mt-2 tracking-wider">
                {total} {total === 1 ? 'Review' : 'Reviews'}
              </p>
            </div>

            {/* Histogram */}
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(star => {
                const count = ratingBreakdown[star] || 0;
                const pct = total > 0 ? (count / total) * 100 : 0;
                return (
                  <div key={star} className="flex items-center gap-2">
                    <span className="text-xs font-sans text-lux-secondary w-4 text-right">{star}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#C6A75E" stroke="#C6A75E" strokeWidth="1.5">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-lux-gold rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] font-sans text-lux-secondary w-6 text-right">{count}</span>
                  </div>
                );
              })}
            </div>

            {/* Write Review CTA */}
            <div className="mt-8">
              {canReview ? (
                <button onClick={() => setShowForm(!showForm)}
                  className="w-full btn-primary text-center text-xs">
                  {showForm ? 'Cancel' : 'Write a Review'}
                </button>
              ) : user ? (
                <p className="text-[10px] text-lux-secondary text-center tracking-wider">
                  Purchase & receive this product to leave a review
                </p>
              ) : (
                <button onClick={() => navigate('/login')}
                  className="w-full btn-outline text-center text-xs">
                  Sign in to Review
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right: Review Form + List */}
        <div className="lg:col-span-2">
          {/* Review Message */}
          {reviewMsg.text && (
            <div className={`mb-4 px-4 py-3 text-xs font-sans ${reviewMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {reviewMsg.text}
            </div>
          )}

          {/* Write Review Form */}
          {showForm && (
            <form onSubmit={handleSubmitReview} className="bg-white border border-lux-border p-8 mb-8 animate-fade-in">
              <h3 className="font-display text-xl text-lux-primary mb-6">Share Your Experience</h3>

              {/* Star Selector */}
              <div className="mb-6">
                <p className="label-sm text-lux-secondary mb-2">Your Rating *</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button key={star} type="button"
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setFormRating(star)}
                      className="transition-transform hover:scale-110">
                      <svg width="28" height="28" viewBox="0 0 24 24"
                        fill={star <= (hoverRating || formRating) ? "#C6A75E" : "none"}
                        stroke={star <= (hoverRating || formRating) ? "#C6A75E" : "#D1CBC0"}
                        strokeWidth="1.5"
                        className="transition-colors duration-150">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </button>
                  ))}
                  {formRating > 0 && (
                    <span className="text-xs font-sans text-lux-secondary ml-2 self-center">
                      {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][formRating]}
                    </span>
                  )}
                </div>
              </div>

              {/* Title */}
              <div className="mb-4">
                <label className="label-sm text-lux-secondary block mb-1.5">Review Title</label>
                <input type="text" value={formTitle} onChange={e => setFormTitle(e.target.value)}
                  className="w-full border border-lux-border bg-white text-sm font-sans p-3 focus:outline-none focus:border-lux-primary"
                  placeholder="Summarize your experience" maxLength={120} />
              </div>

              {/* Comment */}
              <div className="mb-4">
                <label className="label-sm text-lux-secondary block mb-1.5">Your Review</label>
                <textarea value={formComment} onChange={e => setFormComment(e.target.value)}
                  className="w-full border border-lux-border bg-white text-sm font-sans p-3 focus:outline-none focus:border-lux-primary resize-none"
                  rows={4} placeholder="Tell others about the quality, fit, and your overall experience..." maxLength={2000} />
              </div>

              {/* Images */}
              <div className="mb-6">
                <label className="label-sm text-lux-secondary block mb-1.5">Photos (image URLs, one per line)</label>
                <textarea value={formImages} onChange={e => setFormImages(e.target.value)}
                  className="w-full border border-lux-border bg-white text-sm font-sans p-3 focus:outline-none focus:border-lux-primary resize-none"
                  rows={2} placeholder="https://drive.google.com/file/d/..." />
              </div>

              <button type="submit" disabled={submitting}
                className="btn-primary px-8 text-xs disabled:opacity-50">
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          )}

          {/* Sort & Filter Controls */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <select value={sort} onChange={e => setSort(e.target.value)}
              className="border border-lux-border bg-white text-xs font-sans p-2 focus:outline-none focus:border-lux-primary">
              <option value="recent">Most Recent</option>
              <option value="highest">Highest Rated</option>
              <option value="lowest">Lowest Rated</option>
            </select>
            <button onClick={() => setFilter(filter === 'withPhotos' ? '' : 'withPhotos')}
              className={`text-[10px] font-sans tracking-widest uppercase px-3 py-2 border transition-colors ${filter === 'withPhotos' ? 'border-lux-gold bg-lux-gold/10 text-lux-gold' : 'border-lux-border text-lux-secondary hover:border-lux-primary'}`}>
              📷 With Photos
            </button>
          </div>

          {/* Reviews List */}
          {loadingReviews && reviews.length === 0 ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => <div key={i} className="skeleton h-32" />)}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-16 bg-white border border-lux-border">
              <p className="font-display text-2xl text-lux-primary mb-2">No Reviews Yet</p>
              <p className="text-sm font-sans text-lux-secondary">Be the first to share your experience</p>
            </div>
          ) : (
            <div className="space-y-0">
              {reviews.map(review => (
                <div key={review._id} className="bg-white border border-lux-border p-6 mb-[-1px]">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-sans font-medium text-lux-primary">
                          {review.user?.firstName} {review.user?.lastName}
                        </span>
                        {review.isVerified && (
                          <span className="text-[9px] font-sans tracking-widest uppercase bg-green-50 text-green-700 px-1.5 py-0.5 border border-green-200">
                            ✓ Verified Purchase
                          </span>
                        )}
                      </div>
                      <StarDisplay rating={review.rating} size={14} />
                    </div>
                    <span className="text-[10px] font-sans text-lux-secondary">
                      {new Date(review.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  </div>

                  {/* Title & Comment */}
                  {review.title && (
                    <p className="text-sm font-sans font-semibold text-lux-primary mb-1">{review.title}</p>
                  )}
                  {review.comment && (
                    <p className="text-sm font-sans text-lux-secondary leading-relaxed mb-3">{review.comment}</p>
                  )}

                  {/* Review Images */}
                  {review.images?.length > 0 && (
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {review.images.map((img, idx) => (
                        <a key={idx} href={formatImageUrl(img)} target="_blank" rel="noopener noreferrer">
                          <img src={formatImageUrl(img)} alt="Review"
                            className="w-16 h-16 object-cover border border-lux-border hover:border-lux-gold transition-colors"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Seller Reply */}
                  {review.sellerReply && (
                    <div className="mt-3 pl-4 border-l-2 border-lux-gold bg-lux-bg/50 p-3">
                      <p className="text-[10px] font-sans tracking-widest uppercase text-lux-gold mb-1">Seller Response</p>
                      <p className="text-sm font-sans text-lux-secondary">{review.sellerReply}</p>
                    </div>
                  )}
                </div>
              ))}

              {/* Load More */}
              {page < pages && (
                <div className="text-center pt-6">
                  <button onClick={() => fetchReviews(page + 1)}
                    className="btn-outline text-xs px-8" disabled={loadingReviews}>
                    {loadingReviews ? 'Loading...' : 'Load More Reviews'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

